import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getWatchBrands,
  updateWatchBrand,
  getReferencesCountForBrand,
} from '../../services/firebase';
import type { WatchBrand } from '../../types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';

interface PromotedRow {
  brand: WatchBrand;
  order: number;
  featuredCount: number; // How many refs to show in carousel
  refCount: number | null;
}

export function PromotedBrandsManager() {
  const [allBrands, setAllBrands] = useState<WatchBrand[]>([]);
  const [rows, setRows] = useState<PromotedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSearch, setAddSearch] = useState('');

  const loadBrands = async () => {
    setLoading(true);
    try {
      const brands = await getWatchBrands();
      setAllBrands(brands);

      const promoted = brands.filter((b) => b.isPromoted === true);
      promoted.sort((a, b) => (a.promotedOrder ?? 9999) - (b.promotedOrder ?? 9999));

      const initialRows: PromotedRow[] = promoted.map((brand, idx) => ({
        brand,
        order: brand.promotedOrder ?? idx,
        featuredCount: brand.promotedRefCount ?? 6,
        refCount: null,
      }));
      setRows(initialRows);
      setLoading(false);

      // Load ref counts progressively
      const BATCH_SIZE = 5;
      for (let i = 0; i < initialRows.length; i += BATCH_SIZE) {
        const batch = initialRows.slice(i, i + BATCH_SIZE);
        const counts = await Promise.all(
          batch.map(async (row) => {
            try {
              return await getReferencesCountForBrand(row.brand.id);
            } catch {
              return 0;
            }
          }),
        );
        setRows((prev) => {
          const next = [...prev];
          batch.forEach((row, j) => {
            const idx = next.findIndex((r) => r.brand.id === row.brand.id);
            if (idx >= 0) next[idx] = { ...next[idx], refCount: counts[j] };
          });
          return next;
        });
      }
    } catch (err) {
      console.error('Failed to load brands:', err);
      toast.error('Failed to load brands');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, []);

  const handleOrderChange = (brandId: string, newOrder: number) => {
    setRows((prev) =>
      prev.map((r) => (r.brand.id === brandId ? { ...r, order: newOrder } : r)),
    );
  };

  const moveRow = (brandId: string, direction: -1 | 1) => {
    setRows((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((r) => r.brand.id === brandId);
      if (idx < 0) return prev;
      const swapIdx = idx + direction;
      if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
      const a = sorted[idx];
      const b = sorted[swapIdx];
      const newOrderA = b.order;
      const newOrderB = a.order;
      return prev.map((r) => {
        if (r.brand.id === a.brand.id) return { ...r, order: newOrderA };
        if (r.brand.id === b.brand.id) return { ...r, order: newOrderB };
        return r;
      });
    });
  };

  const handleSaveOrder = async (row: PromotedRow) => {
    setSavingId(row.brand.id);
    try {
      await updateWatchBrand(row.brand.id, { promotedOrder: row.order, promotedRefCount: row.featuredCount });
      toast.success(`Updated ${row.brand.name}`);
    } catch (err) {
      console.error('Failed to update order:', err);
      toast.error('Failed to save order');
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveAll = async () => {
    const t = toast.loading('Saving all changes…');
    try {
      for (const row of rows) {
        const orderChanged = (row.brand.promotedOrder ?? null) !== row.order;
        const countChanged = (row.brand.promotedRefCount ?? 6) !== row.featuredCount;
        if (orderChanged || countChanged) {
          await updateWatchBrand(row.brand.id, { promotedOrder: row.order, promotedRefCount: row.featuredCount });
        }
      }
      toast.dismiss(t);
      toast.success('Saved all changes');
      await loadBrands();
    } catch (err) {
      toast.dismiss(t);
      console.error('Failed to save all:', err);
      toast.error('Failed to save all');
    }
  };

  const handleDisable = async (brand: WatchBrand) => {
    if (!confirm(`Remove "${brand.name}" from promoted brands?`)) return;
    setSavingId(brand.id);
    try {
      await updateWatchBrand(brand.id, { isPromoted: false });
      setRows((prev) => prev.filter((r) => r.brand.id !== brand.id));
      setAllBrands((prev) =>
        prev.map((b) => (b.id === brand.id ? { ...b, isPromoted: false } : b)),
      );
      toast.success(`Removed "${brand.name}" from promotions`);
    } catch (err) {
      console.error('Failed to disable:', err);
      toast.error('Failed to remove from promotions');
    } finally {
      setSavingId(null);
    }
  };

  const handleAddBrand = async (brand: WatchBrand) => {
    setSavingId(brand.id);
    try {
      const maxOrder = rows.reduce((m, r) => Math.max(m, r.order), -1);
      const newOrder = maxOrder + 1;
      await updateWatchBrand(brand.id, {
        isPromoted: true,
        promotedOrder: newOrder,
      });
      const updated: WatchBrand = { ...brand, isPromoted: true, promotedOrder: newOrder };
      setRows((prev) => [...prev, { brand: updated, order: newOrder, featuredCount: 6, refCount: null }]);
      setAllBrands((prev) =>
        prev.map((b) => (b.id === brand.id ? updated : b)),
      );
      toast.success(`Promoted "${brand.name}"`);
      setAddModalOpen(false);
      setAddSearch('');

      // Load ref count in background
      try {
        const count = await getReferencesCountForBrand(brand.id);
        setRows((prev) =>
          prev.map((r) => (r.brand.id === brand.id ? { ...r, refCount: count } : r)),
        );
      } catch {
        /* ignore */
      }
    } catch (err) {
      console.error('Failed to promote brand:', err);
      toast.error('Failed to promote brand');
    } finally {
      setSavingId(null);
    }
  };

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => a.order - b.order),
    [rows],
  );

  const hasUnsavedChanges = useMemo(
    () =>
      rows.some(
        (r) => (r.brand.promotedOrder ?? null) !== r.order || (r.brand.promotedRefCount ?? 6) !== r.featuredCount,
      ),
    [rows],
  );

  const candidateBrands = useMemo(() => {
    const promotedIds = new Set(rows.map((r) => r.brand.id));
    const q = addSearch.trim().toLowerCase();
    return allBrands
      .filter((b) => !promotedIds.has(b.id))
      .filter((b) =>
        q === '' ? true : b.name.toLowerCase().includes(q),
      )
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, 100);
  }, [allBrands, rows, addSearch]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promoted Brands</h1>
          <p className="text-gray-500 mt-1">
            Brands featured in Explore carousels and brand lists. Lower order = higher priority.
          </p>
        </div>
        <div className="flex gap-2">
          {hasUnsavedChanges && (
            <Button variant="success" onClick={handleSaveAll}>
              Save Order Changes
            </Button>
          )}
          <Button onClick={() => setAddModalOpen(true)}>+ Add Brand</Button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">
                Order
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Brand
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24">
                References
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-24" title="Number of references shown in the Featured carousel">
                Featured
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-64">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sortedRows.map((row) => {
              const isDirty = (row.brand.promotedOrder ?? null) !== row.order || (row.brand.promotedRefCount ?? 6) !== row.featuredCount;
              return (
                <tr key={row.brand.id} className={isDirty ? 'bg-amber-50' : ''}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveRow(row.brand.id, -1)}
                        className="text-gray-400 hover:text-gray-700 px-1.5 py-0.5 rounded hover:bg-gray-100"
                        aria-label="Move up"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => moveRow(row.brand.id, 1)}
                        className="text-gray-400 hover:text-gray-700 px-1.5 py-0.5 rounded hover:bg-gray-100"
                        aria-label="Move down"
                      >
                        ▼
                      </button>
                      <input
                        type="number"
                        value={row.order}
                        onChange={(e) =>
                          handleOrderChange(row.brand.id, Number(e.target.value))
                        }
                        className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">
                      {row.brand.name}
                    </div>
                    <div className="text-xs text-gray-500">{row.brand.id}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-sm text-gray-600">
                    {row.refCount === null ? (
                      <span className="text-gray-300">…</span>
                    ) : (
                      row.refCount
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="number"
                      value={row.featuredCount}
                      onChange={(e) => {
                        const val = Math.max(1, Math.min(20, Number(e.target.value) || 6));
                        setRows(prev => prev.map(r => r.brand.id === row.brand.id ? { ...r, featuredCount: val } : r));
                      }}
                      className="w-14 border border-gray-300 rounded px-2 py-1 text-sm text-center"
                      min={1}
                      max={20}
                      title="Number of references to show in Featured carousel"
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {isDirty && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleSaveOrder(row)}
                          loading={savingId === row.brand.id}
                        >
                          Save
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisable(row.brand)}
                        loading={savingId === row.brand.id}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remove
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {sortedRows.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No promoted brands yet — click "+ Add Brand" to promote one.
          </div>
        )}
      </div>

      <Modal
        isOpen={addModalOpen}
        onClose={() => {
          setAddModalOpen(false);
          setAddSearch('');
        }}
        title="Promote a Brand"
        size="md"
      >
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Search brands…"
            value={addSearch}
            onChange={(e) => setAddSearch(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            autoFocus
          />
          <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
            {candidateBrands.length === 0 && (
              <div className="text-center py-4 text-sm text-gray-500">
                {addSearch ? 'No matches' : 'No brands available'}
              </div>
            )}
            {candidateBrands.map((brand) => (
              <button
                key={brand.id}
                onClick={() => handleAddBrand(brand)}
                disabled={savingId === brand.id}
                className="w-full text-left px-4 py-2 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-between"
              >
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {brand.name}
                  </div>
                  <div className="text-xs text-gray-500">{brand.id}</div>
                </div>
                {savingId === brand.id && (
                  <span className="text-xs text-gray-500">Adding…</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}
